import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from 'react-query';
import { ordersAPI } from '../services/api';
import { 
  Search, 
  Filter, 
  Eye, 
  ShoppingCart,
  Calendar,
  X,
  Package,
  DollarSign,
  TrendingUp,
  Download,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Badge from '../components/UI/Badge';
import Pagination from '../components/UI/Pagination';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Orders = () => {
  const { isDark } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  
  const { data, isLoading, error } = useQuery(
    ['orders', { 
      page: currentPage, 
      search: searchQuery, 
      status: statusFilter,
      paymentStatus: paymentFilter
    }],
    () => ordersAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchQuery,
      status: statusFilter,
      paymentStatus: paymentFilter
    }),
    {
      keepPreviousData: true,
    }
  );

  const orders = data?.data?.orders || [];
  const pagination = data?.data?.pagination || {};

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPaymentFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || statusFilter || paymentFilter;

  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const downloadMenuRef = useRef(null);

  // Close download menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchExportData = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (paymentFilter) params.paymentStatus = paymentFilter;
    if (searchQuery) params.search = searchQuery;
    const res = await ordersAPI.exportOrders(params);
    return res.data;
  };

  const downloadAsPDF = async () => {
    setIsExporting(true);
    setShowDownloadMenu(false);
    try {
      const exportData = await fetchExportData();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.text('Beetle Diffuser - Orders Report', 14, 16);
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Exported: ${new Date().toLocaleString()}  |  Total Orders: ${exportData.total}`, 14, 23);
      doc.setDrawColor(37, 99, 235);
      doc.setLineWidth(0.5);
      doc.line(14, 25, pageWidth - 14, 25);

      // --- ORDERS SUMMARY TABLE ---
      doc.setFontSize(13);
      doc.setTextColor(40, 40, 40);
      doc.text('Orders Summary', 14, 33);

      const summaryRows = exportData.orders.map((o) => [
        o.orderNumber,
        o.customerName,
        o.customerPhone || '-',
        o.itemsSummary || '-',
        `$${Number(o.totalAmount).toFixed(2)}`,
        (o.status || '').charAt(0).toUpperCase() + (o.status || '').slice(1),
        (o.paymentStatus || '').charAt(0).toUpperCase() + (o.paymentStatus || '').slice(1),
        o.paymentMethod || '-',
        o.shippingFullAddress || '-',
        o.trackingNumber || '-',
        o.orderDate ? new Date(o.orderDate).toLocaleDateString() : '-'
      ]);

      autoTable(doc, {
        startY: 36,
        head: [['Order #', 'Customer', 'Phone', 'Items', 'Total', 'Status', 'Payment', 'Method', 'Ship To', 'Tracking', 'Date']],
        body: summaryRows,
        styles: { fontSize: 6.5, cellPadding: 1.5 },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
          0: { cellWidth: 18 }, 1: { cellWidth: 25 }, 2: { cellWidth: 22 },
          3: { cellWidth: 40 }, 4: { cellWidth: 18 }, 5: { cellWidth: 18 },
          6: { cellWidth: 16 }, 7: { cellWidth: 16 }, 8: { cellWidth: 45 },
          9: { cellWidth: 18 }, 10: { cellWidth: 18 }
        },
        margin: { left: 8, right: 8 },
        theme: 'grid',
      });

      // --- DETAILED ORDER SECTION ---
      let currentY = doc.lastAutoTable.finalY + 12;

      exportData.orders.forEach((order) => {
        // Check if we need a new page
        if (currentY > 170) {
          doc.addPage();
          currentY = 15;
        }

        // Order header
        doc.setFillColor(37, 99, 235);
        doc.rect(8, currentY - 4, pageWidth - 16, 8, 'F');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(`Order: ${order.orderNumber}  |  ${order.customerName}  |  ${order.orderDate ? new Date(order.orderDate).toLocaleString() : ''}`, 12, currentY + 1);
        currentY += 8;

        // Customer & Address info
        doc.setFontSize(8);
        doc.setTextColor(60, 60, 60);
        const infoLines = [
          `Customer: ${order.customerName} (${order.customerType})  |  Email: ${order.customerEmail}  |  Phone: ${order.customerPhone}`,
          `Shipping: ${order.shippingFullAddress || 'N/A'}  |  Phone: ${order.shippingPhone || '-'}`,
          order.billingFullAddress ? `Billing: ${order.billingFullAddress}` : null,
          `Status: ${order.status}  |  Payment: ${order.paymentStatus} (${order.paymentMethod || '-'})  |  Tracking: ${order.trackingNumber || '-'}`,
        ].filter(Boolean);
        infoLines.forEach(line => {
          doc.text(line, 12, currentY);
          currentY += 4;
        });
        if (order.notes) {
          doc.text(`Notes: ${order.notes}`, 12, currentY);
          currentY += 4;
        }
        currentY += 1;

        // Items table
        const itemRows = order.items.map((item, i) => [
          i + 1,
          item.name,
          item.sku || '-',
          item.size || '-',
          item.quantity,
          `$${Number(item.price).toFixed(2)}`,
          `$${Number(item.lineTotal).toFixed(2)}`
        ]);

        autoTable(doc, {
          startY: currentY,
          head: [['#', 'Product', 'SKU', 'Size', 'Qty', 'Price', 'Line Total']],
          body: itemRows,
          styles: { fontSize: 7, cellPadding: 1.2 },
          headStyles: { fillColor: [80, 80, 80], textColor: 255, fontStyle: 'bold', fontSize: 7 },
          alternateRowStyles: { fillColor: [248, 248, 248] },
          margin: { left: 10, right: 10 },
          theme: 'grid',
        });

        currentY = doc.lastAutoTable.finalY + 2;

        // Pricing summary
        doc.setFontSize(7);
        doc.setTextColor(80, 80, 80);
        const priceX = pageWidth - 70;
        const pricingLines = [
          ['Subtotal:', `$${Number(order.subtotal).toFixed(2)}`],
          ['Tax:', `$${Number(order.tax).toFixed(2)}`],
          ['Shipping:', `$${Number(order.shipping).toFixed(2)}`],
          ['Discount:', `-$${Number(order.discount).toFixed(2)}`],
        ];
        pricingLines.forEach(([label, val]) => {
          doc.text(label, priceX, currentY);
          doc.text(val, priceX + 35, currentY);
          currentY += 3.5;
        });
        doc.setFontSize(8);
        doc.setTextColor(37, 99, 235);
        doc.text('TOTAL:', priceX, currentY);
        doc.text(`$${Number(order.totalAmount).toFixed(2)}`, priceX + 35, currentY);
        currentY += 10;
      });

      doc.save(`orders_detailed_report_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success(`Exported ${exportData.total} orders as PDF`);
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF: ' + (err.message || err));
    } finally {
      setIsExporting(false);
    }
  };

  const downloadAsExcel = async () => {
    setIsExporting(true);
    setShowDownloadMenu(false);
    try {
      const exportData = await fetchExportData();

      // Sheet 1: Orders Summary (one row per order)
      const ordersData = exportData.orders.map((o) => ({
        'Order #': o.orderNumber,
        'Order Date': o.orderDate ? new Date(o.orderDate).toLocaleString() : '',
        'Last Updated': o.lastUpdated ? new Date(o.lastUpdated).toLocaleString() : '',
        'Customer Type': o.customerType,
        'Customer Name': o.customerName,
        'Email': o.customerEmail,
        'Phone': o.customerPhone,
        'Items Summary': o.itemsSummary,
        'Total Items': o.totalItems,
        'Subtotal ($)': Number(o.subtotal).toFixed(2),
        'Tax ($)': Number(o.tax).toFixed(2),
        'Shipping ($)': Number(o.shipping).toFixed(2),
        'Discount ($)': Number(o.discount).toFixed(2),
        'Total ($)': Number(o.totalAmount).toFixed(2),
        'Order Status': o.status,
        'Payment Status': o.paymentStatus,
        'Payment Method': o.paymentMethod || '',
        'Tracking #': o.trackingNumber || '',
        'Ship To - Name': o.shippingName || '',
        'Ship To - Street': o.shippingStreet || '',
        'Ship To - City': o.shippingCity || '',
        'Ship To - State': o.shippingState || '',
        'Ship To - Zip': o.shippingZipCode || '',
        'Ship To - Country': o.shippingCountry || '',
        'Ship To - Phone': o.shippingPhone || '',
        'Ship To - Full Address': o.shippingFullAddress || '',
        'Bill To - Full Address': o.billingFullAddress || '',
        'Notes': o.notes || '',
      }));

      // Sheet 2: Order Items (one row per item)
      const itemsData = [];
      exportData.orders.forEach(o => {
        (o.items || []).forEach((item, idx) => {
          itemsData.push({
            'Order #': o.orderNumber,
            'Order Date': o.orderDate ? new Date(o.orderDate).toLocaleString() : '',
            'Customer': o.customerName,
            'Item #': idx + 1,
            'Product Name': item.name,
            'SKU': item.sku || '',
            'Size': item.size || '',
            'Quantity': item.quantity,
            'Unit Price ($)': Number(item.price).toFixed(2),
            'Line Total ($)': Number(item.lineTotal).toFixed(2),
            'Product ID': item.productId || '',
          });
        });
      });

      // Auto-width function
      const autoWidth = (data) => {
        if (!data.length) return [];
        return Object.keys(data[0]).map(key => ({
          wch: Math.max(key.length, ...data.map(r => String(r[key] || '').length).slice(0, 100)) + 2
        }));
      };

      const wb = XLSX.utils.book_new();

      const ws1 = XLSX.utils.json_to_sheet(ordersData);
      ws1['!cols'] = autoWidth(ordersData);
      XLSX.utils.book_append_sheet(wb, ws1, 'Orders');

      if (itemsData.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(itemsData);
        ws2['!cols'] = autoWidth(itemsData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Order Items');
      }

      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `orders_detailed_report_${new Date().toISOString().slice(0,10)}.xlsx`);
      toast.success(`Exported ${exportData.total} orders as Excel (2 sheets)`);
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Failed to export Excel: ' + (err.message || err));
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      confirmed: 'info',
      processing: 'info',
      shipped: 'info',
      delivered: 'success',
      cancelled: 'error',
      refunded: 'error'
    };
    return <Badge variant={variants[status] || 'default'} dot>{status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const variants = {
      pending: 'warning',
      paid: 'success',
      failed: 'error',
      refunded: 'error'
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' }
  ];

  const paymentOptions = [
    { value: '', label: 'All Payments' },
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' }
  ];

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className={`text-center backdrop-blur-sm border rounded-2xl p-8 max-w-md ${
          isDark ? 'bg-dark-800/50 border-dark-700/50' : 'bg-white/70 border-gray-200'
        }`}>
          <div className="w-16 h-16 bg-error-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="h-8 w-8 text-error-400" />
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Error loading orders</h3>
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Orders</h1>
          <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manage customer orders and track fulfillment</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-2 backdrop-blur-sm border rounded-xl ${
            isDark ? 'bg-dark-800/50 border-dark-700/50' : 'bg-white/70 border-gray-200'
          }`}>
            <Package className="h-4 w-4 text-primary-400" />
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{pagination.total || 0} total</span>
          </div>

          {/* Download Button */}
          <div className="relative" ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={isExporting}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                isExporting
                  ? 'opacity-60 cursor-not-allowed'
                  : isDark
                    ? 'bg-primary-500/20 text-primary-400 border-primary-500/30 hover:bg-primary-500/30'
                    : 'bg-primary-50 text-primary-600 border-primary-200 hover:bg-primary-100'
              }`}
            >
              {isExporting ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isExporting ? 'Exporting...' : 'Export'}
            </button>

            {showDownloadMenu && (
              <div className={`absolute right-0 mt-2 w-52 rounded-xl shadow-xl border z-50 overflow-hidden ${
                isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200'
              }`}>
                <button
                  onClick={downloadAsPDF}
                  className={`flex items-center gap-3 w-full px-4 py-3 text-sm text-left transition-colors ${
                    isDark
                      ? 'text-gray-300 hover:bg-dark-700 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <FileText className="h-4 w-4 text-red-400" />
                  <div>
                    <div className="font-medium">Download PDF</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Print-ready report</div>
                  </div>
                </button>
                <div className={`border-t ${isDark ? 'border-dark-700' : 'border-gray-100'}`} />
                <button
                  onClick={downloadAsExcel}
                  className={`flex items-center gap-3 w-full px-4 py-3 text-sm text-left transition-colors ${
                    isDark
                      ? 'text-gray-300 hover:bg-dark-700 hover:text-white'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <FileSpreadsheet className="h-4 w-4 text-green-400" />
                  <div>
                    <div className="font-medium">Download Excel</div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Spreadsheet backup</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-2">
        {statusOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => { setStatusFilter(opt.value === statusFilter ? '' : opt.value); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              statusFilter === opt.value
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                : isDark 
                  ? 'bg-dark-800/50 text-gray-400 border border-dark-700/50 hover:bg-dark-700/50 hover:text-white'
                  : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Search & Advanced Filters */}
      <div className={`backdrop-blur-sm border rounded-2xl p-4 ${
        isDark ? 'bg-dark-800/50 border-dark-700/50' : 'bg-white/70 border-gray-200'
      }`}>
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search by order number, customer name or email..."
                className={`w-full px-4 py-3 pl-11 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all ${
                  isDark 
                    ? 'bg-dark-700/50 border-dark-600 text-white placeholder-gray-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <select
            className={`px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all appearance-none cursor-pointer ${
              isDark 
                ? 'bg-dark-700/50 border-dark-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            value={paymentFilter}
            onChange={(e) => {
              setPaymentFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            {paymentOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          
          <button type="submit" className="btn btn-secondary">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </button>
        </form>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className={`flex flex-wrap items-center gap-2 mt-4 pt-4 border-t ${isDark ? 'border-dark-700/50' : 'border-gray-200'}`}>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Active filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-500/20 text-primary-400 rounded-lg text-sm">
                Search: {searchQuery}
                <button onClick={() => setSearchQuery('')} className="hover:text-primary-300">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-500/20 text-accent-400 rounded-lg text-sm capitalize">
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('')} className="hover:text-accent-300">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            {paymentFilter && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-success-500/20 text-success-400 rounded-lg text-sm capitalize">
                Payment: {paymentFilter}
                <button onClick={() => setPaymentFilter('')} className="hover:text-success-300">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
            <button
              onClick={clearFilters}
              className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Orders Table */}
      <div className={`backdrop-blur-sm border rounded-2xl overflow-hidden ${
        isDark ? 'bg-dark-800/50 border-dark-700/50' : 'bg-white/70 border-gray-200'
      }`}>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : orders.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isDark ? 'bg-dark-700/50' : 'bg-gray-100'
              }`}>
                <ShoppingCart className={`h-8 w-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>No orders found</h3>
              <p className={`text-sm max-w-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {hasActiveFilters 
                  ? 'Try adjusting your filters to find what you\'re looking for.'
                  : 'Orders will appear here when customers make purchases.'}
              </p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="btn btn-secondary mt-4">
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-dark-700/50' : 'border-gray-200'}`}>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Order</th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Customer</th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Items</th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Payment</th>
                    <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-dark-700/30' : 'divide-gray-100'}`}>
                  {orders.map((order) => (
                    <tr key={order._id} className={`transition-colors ${isDark ? 'hover:bg-dark-700/30' : 'hover:bg-gray-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                            <ShoppingCart className="h-5 w-5 text-primary-400" />
                          </div>
                          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            #{order.orderNumber}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {order.customer?.name || order.guestCustomer?.name || 'N/A'}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {order.customer?.email || order.guestCustomer?.email || '-'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Calendar className={`h-4 w-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                          {new Date(order.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <span className={`px-2 py-1 rounded-lg ${isDark ? 'bg-dark-700/50' : 'bg-gray-100'}`}>
                            {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <DollarSign className="h-4 w-4 text-success-400" />
                          {order.totalAmount.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentBadge(order.paymentStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/orders/${order._id}`}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all text-sm ${
                            isDark 
                              ? 'bg-dark-700/50 hover:bg-primary-500/20 text-gray-300 hover:text-primary-400'
                              : 'bg-gray-100 hover:bg-primary-50 text-gray-600 hover:text-primary-600'
                          }`}
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className={`border-t ${isDark ? 'border-dark-700/50' : 'border-gray-200'}`}>
              <Pagination
                currentPage={pagination.page || 1}
                totalPages={pagination.pages || 1}
                onPageChange={setCurrentPage}
                totalItems={pagination.total || 0}
                itemsPerPage={pagination.limit || 10}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Orders;